import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Box,
  Typography,
  Button,
  TextField,
  Modal,
  CircularProgress,
  Snackbar,
  Alert,
  Dialog,
  DialogContent,
  IconButton,
} from "@mui/material";
import { useDropzone } from "react-dropzone";
import CloseIcon from "@mui/icons-material/Close";
import api, { API_BASE_URL } from "../../../utils/api";

const ViolationComments = ({ violation_id, currentUserId, event_id = null }) => {
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [imageURL, setImageURL] = useState(null);
  const [openImageModal, setOpenImageModal] = useState(false);

  useEffect(() => {
    const fetchComments = async () => {
      setIsLoading(true);
      try {
        const response = await axios.get(
          `${API_BASE_URL}/getExternalViolationComments/${violation_id}${event_id ? `/${event_id}` : ""}`
        );
        setComments(response.data);
      } catch (error) {
        console.error("Failed to fetch comments:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchComments();
  }, [violation_id, event_id]);

  const handleAddComment = async () => {
    if (!newComment.trim()) {
      alert("Comment cannot be empty.");
      return;
    }

    try {
      const formData = new FormData();
      formData.append("violation_id", violation_id);
      formData.append("user_id", currentUserId);
      formData.append("comment_text", newComment);
      formData.append("is_internal", false);
      if (event_id) formData.append("event_id", event_id);
      if (selectedFile) formData.append("upload_urls", selectedFile);

      await axios.post(`${API_BASE_URL}/addViolationComment`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setComments((prev) => [
        {
          comment_text: newComment,
          sent_at: new Date().toISOString(),
          user_name: "You",
          user_lastname: "",
          upload_urls: selectedFile?.name,
        },
        ...prev,
      ]);

      setNewComment("");
      setSelectedFile(null);
      setFilePreview(null);
      setIsModalOpen(false);
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Failed to add comment:", error);
      alert("Failed to add comment. Please try again.");
    }
  };

  const { getRootProps, getInputProps } = useDropzone({
    accept: "image/*",
    multiple: false,
    onDrop: (acceptedFiles) => {
      if (acceptedFiles.length > 0) {
        const file = acceptedFiles[0];
        setSelectedFile(file);
        setFilePreview(URL.createObjectURL(file));
      }
    },
  });

  const handleSnackbarClose = () => setSnackbarOpen(false);

  const handleImageClick = (filename) => {
    const url = `${API_BASE_URL}/violationsimages/${encodeURIComponent(filename)}`;
    setImageURL(url);
    setOpenImageModal(true);
  };

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", marginTop: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Comments
      </Typography>
      {comments.length > 0 ? (
        comments.map((comment, index) => (
          <Box
            key={index}
            sx={{
              mb: 2,
              p: 2,
              border: "1px solid #ddd",
              borderRadius: "8px",
              backgroundColor: "#e3f2fd",
            }}
          >
            <Typography variant="body2" sx={{ mb: comment.upload_urls ? 1 : 0 }}>
              {comment.comment_text}
            </Typography>

            {comment.upload_urls && (
  <Box
    sx={{ mt: 1, textAlign: "left", cursor: "pointer" }}
    onClick={() => handleImageClick(comment.upload_urls)}
  >
    <img
      src={`${API_BASE_URL}/violationsimages/${encodeURIComponent(comment.upload_urls)}`}
      alt="Comment Attachment"
      style={{ maxWidth: "100%", maxHeight: 150, borderRadius: 8, border: "1px solid #ccc" }}
    />
    <Typography variant="caption" sx={{ fontStyle: "italic" }}>
      Click to enlarge
    </Typography>
  </Box>
)}


            <Typography variant="caption" color="textSecondary">
              Added by {comment.user_name} {comment.user_lastname || ""} on{" "}
              {new Date(comment.sent_at).toLocaleString()}
            </Typography>
          </Box>
        ))
      ) : (
        <Typography>No comments available.</Typography>
      )}

      <Button variant="contained" color="primary" onClick={() => setIsModalOpen(true)}>
        Add Comment
      </Button>

      <Modal open={isModalOpen} onClose={() => setIsModalOpen(false)} aria-labelledby="add-comment-modal">
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            backgroundColor: "white",
            padding: 4,
            borderRadius: 2,
            boxShadow: 24,
            maxHeight: "90vh",
            overflowY: "auto",
          }}
        >
          <Typography id="add-comment-modal" variant="h6" sx={{ mb: 2 }}>
            Add Comment
          </Typography>
          <TextField
            fullWidth
            label="Comment"
            multiline
            rows={3}
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            sx={{ mb: 2 }}
          />

          <Box
            {...getRootProps()}
            sx={{
              border: "2px dashed #ccc",
              borderRadius: "8px",
              padding: "20px",
              textAlign: "center",
              cursor: "pointer",
              mb: 2,
            }}
          >
            <input {...getInputProps()} />
            <Typography>Drag & drop an image, or click to select</Typography>
          </Box>

          {filePreview && (
            <Box sx={{ textAlign: "center", mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                📎 Preview:
              </Typography>
              <img src={filePreview} alt="Preview" style={{ maxWidth: "100%", maxHeight: 150 }} />
            </Box>
          )}

          <Button fullWidth variant="contained" color="primary" onClick={handleAddComment}>
            Save Comment
          </Button>
        </Box>
      </Modal>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={handleSnackbarClose} severity="success" sx={{ width: "100%" }}>
          Comment added successfully!
        </Alert>
      </Snackbar>

      {/* Full Image Modal */}
      <Dialog open={openImageModal} onClose={() => setOpenImageModal(false)} maxWidth="md">
        <DialogContent sx={{ position: "relative", textAlign: "center" }}>
          <IconButton
            onClick={() => setOpenImageModal(false)}
            sx={{ position: "absolute", top: 8, right: 8 }}
          >
            <CloseIcon />
          </IconButton>
          <img
            src={imageURL}
            alt="Full View"
            style={{ width: "100%", maxHeight: "90vh", objectFit: "contain" }}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ViolationComments;
